// Session selectors over the shared `@smartsash/auth` client (HS-859, C1).
//
// The package hook returns `{ state, auth }` — a discriminated union, not admin's old
// `{ user, isLoading, error }` bag. Narrowing `state.status === 'authenticated'` in every
// consumer would be noise, so the two reads admin actually makes live here.
//
// These are the ONLY place admin reaches into the session shape; components use `useAuth()`
// directly for the imperative side (`auth.signIn`, `auth.signOut`, `auth.startOAuthRedirect`).

import { permissionsReported, useAuth, type AuthUser } from '@smartsash/auth';

import { firstNonEmpty } from '@/utils/firstNonEmpty';

/** Stable identity for the empty case — `SessionPermissionsProvider` memoises on this reference. */
const NO_PERMISSIONS: readonly string[] = Object.freeze([]);

/** The signed-in principal, or `null` in every other auth state. */
export function useSessionUser(): AuthUser | null {
  const { state } = useAuth();
  return state.status === 'authenticated' ? state.session.user : null;
}

/**
 * Effective permission keys granted by `/Auth/me`.
 *
 * The backend exposes no user-level role — authorization is evaluated on effective
 * permissions, and roles live per assignment — so this is the whole authorization input.
 */
export function useSessionPermissions(): readonly string[] {
  const { state } = useAuth();
  return state.status === 'authenticated'
    ? (state.session.permissions ?? NO_PERMISSIONS)
    : NO_PERMISSIONS;
}

/**
 * Whether `/Auth/me` actually reported this session's permissions (HS-1387).
 *
 * `false` means the list arrived empty from a backend that may simply have declined to send it,
 * which is indistinguishable on the wire from an account that genuinely holds nothing. Every
 * guard refuses either way; the difference is only in what the user is told, and telling them
 * nothing is what made a withheld list look like an app that had lost its contents.
 *
 * `true` while signed out: there is no session, so there is nothing to explain.
 */
export function useSessionPermissionsReported(): boolean {
  const { state } = useAuth();
  return state.status === 'authenticated' ? permissionsReported(state.session) : true;
}

/**
 * "First Last", falling back to the e-mail, then to an empty string.
 *
 * Both the sidebar account card and the dashboard greeting render this; keeping one
 * implementation stops the two from drifting apart.
 */
export function sessionDisplayName(user: AuthUser | null): string {
  if (!user) return '';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return firstNonEmpty(fullName, user.email);
}
